import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../services/users';
import { getAllBilling, getMrrHistory } from '../services/billing';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  SectionTitle,
  StatsGrid,
  StatCard,
  DetailCard,
  TableContainer,
  StyledTable,
  Th,
  Td,
  StatusChip,
  EmptyState,
  LoadingText,
} from '../components/styled';

/* ── page-local styled components ── */

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  padding: ${({ theme }) => theme.spacing(2)} 0;
`;

// SectionTitle has no top margin and DetailCard no bottom margin, so a title
// after a card sits flush against it. Same fix CompanyDetail uses.
const SpacedSectionTitle = styled(SectionTitle)`
  margin-top: ${({ theme }) => theme.spacing(8)};
`;

const CompanyLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

const CustomTooltipWrapper = styled.div`
  background: ${({ theme }) => theme.background.secondary};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  padding: ${({ theme }) => theme.spacing(2, 3)};
  box-shadow: ${({ theme }) => theme.boxShadow?.light || '0 2px 8px rgba(0,0,0,0.1)'};
`;

const TooltipLabel = styled.div`
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

const TooltipRow = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.6;
`;

/* ── helpers ── */

function formatMonth(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function formatCurrency(value) {
  return `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <CustomTooltipWrapper>
      <TooltipLabel>{formatMonth(label)}</TooltipLabel>
      <TooltipRow>MRR: {formatCurrency(data.mrr)}</TooltipRow>
      <TooltipRow>Devices: {data.totalDevices}</TooltipRow>
      <TooltipRow>Software providers: {data.activeCompanies}</TooltipRow>
    </CustomTooltipWrapper>
  );
}

/* ── main component ── */

function Billing() {
  const { user } = useAuth();
  const theme = useTheme();
  const isAdmin = isSuperAdmin(user);

  const { data: billingData, isLoading: billingLoading } = useQuery({
    queryKey: ['billing', 'all'],
    queryFn: getAllBilling,
    enabled: isAdmin,
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['billing', 'mrr-history'],
    queryFn: () => getMrrHistory(12),
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (billingLoading) {
    return (
      <PageContainer>
        <LoadingText>Loading billing data...</LoadingText>
      </PageContainer>
    );
  }

  const { companies = [], totals = {} } = billingData || {};
  const history = historyData?.history || [];

  const currentMrr = totals.totalMonthly || 0;
  const totalDevices = totals.totalDevices || 0;
  const activeCompanies = totals.enabledCompanies || 0;
  const avgPerCompany = activeCompanies > 0 ? currentMrr / activeCompanies : 0;

  // Sort companies by monthly revenue descending
  const sortedCompanies = [...companies]
    .filter((c) => !c.error)
    .sort((a, b) => (b.monthlyTotal || 0) - (a.monthlyTotal || 0));

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Billing</PageTitle>
      </PageHeader>

      {/* Summary Stats */}
      <StatsGrid>
        <StatCard>
          <h3>Current MRR</h3>
          <p className="stat-value">{formatCurrency(currentMrr)}</p>
        </StatCard>
        <StatCard>
          <h3>Deployed Devices</h3>
          <p className="stat-value">{totalDevices}</p>
        </StatCard>
        <StatCard>
          <h3>Active Software Providers</h3>
          <p className="stat-value">{activeCompanies}</p>
        </StatCard>
        <StatCard>
          <h3>Avg Revenue / Company</h3>
          <p className="stat-value">{formatCurrency(avgPerCompany)}</p>
        </StatCard>
      </StatsGrid>

      {/* MRR Chart */}
      <DetailCard>
        <h2>MRR Over Time</h2>
        {historyLoading ? (
          <LoadingText>Loading chart data...</LoadingText>
        ) : history.length === 0 ? (
          <EmptyState>
            <p>No MRR history available yet. Data is derived from billing sync logs.</p>
          </EmptyState>
        ) : (
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.accent.primary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={theme.accent.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.border.color.light} />
                <XAxis
                  dataKey="month"
                  tickFormatter={formatMonth}
                  stroke={theme.font.color.tertiary}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  tickFormatter={(v) => `$${v}`}
                  stroke={theme.font.color.tertiary}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="mrr"
                  stroke={theme.accent.primary}
                  strokeWidth={2}
                  fill="url(#mrrGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </DetailCard>

      {/* Revenue by Company */}
      <SpacedSectionTitle>Revenue by Software Provider</SpacedSectionTitle>
      {sortedCompanies.length === 0 ? (
        <EmptyState>
          <p>No billing data available</p>
        </EmptyState>
      ) : (
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <Th>Company</Th>
                <Th>Method</Th>
                <Th>Deployed Devices</Th>
                <Th>Price/Device</Th>
                <Th>Monthly Revenue</Th>
              </tr>
            </thead>
            <tbody>
              {sortedCompanies.map((company) => (
                <tr key={company.id}>
                  <Td>
                    <CompanyLink to={`/software-providers/${company.id}?tab=billing`}>
                      {company.name}
                    </CompanyLink>
                  </Td>
                  <Td>
                    <StatusChip
                      variant={
                        company.billingMethod === 'stripe'
                          ? 'green'
                          : company.billingMethod === 'odoo'
                            ? 'blue'
                            : 'gray'
                      }
                    >
                      {company.billingMethod || 'None'}
                    </StatusChip>
                  </Td>
                  <Td>{company.deployedDevices || 0}</Td>
                  <Td>{formatCurrency(company.pricePerDevice)}</Td>
                  <Td>{formatCurrency(company.monthlyTotal)}</Td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableContainer>
      )}
    </PageContainer>
  );
}

export default Billing;
